import { moveAssetsToTrash } from "@apis/http/api";
import { showFallbackErrorToast } from "@apis/http/httpErrorToast";
import { CommonButton } from "@components/CommonButton";
import { showTrashDisplay, FocusType, VideoType, ProductType, CharacterType } from "../librarySlice";
import { useAppDispatch } from "@hooks/redux";
import { Tabs, TabOption } from "@src/ui/Tabs";
import classNames from "classnames";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { useLibraryFilters } from "../hooks/useLibraryFilters";
import type { UseImageBatchSelectionReturn } from "../hooks/useImageBatchSelection";
import { Button } from "@kalodata/kds-design";
import { IconLineTrash } from "@kalodata/kds-design-icons";
import { IconBatchDelete } from "@icons/index";
import { useSerializeCallback } from "@kalodata/tools";
import { message } from "antd";

const tabOptions: TabOption[] = [
  { value: "video", label: "Videos", badge: 0 },
  { value: "image", label: "Images" },
  { value: "script", label: "Scripts" },
  { value: "product", label: "Products" },
  { value: "character", label: "Characters" },
];

const contentFilterBaseOptions = [
  { value: "generated", label: "Generated" },
  { value: "uploaded", label: "Uploaded" },
  { value: "saved", label: "Saved" },
];

const productFilterOptions: TabOption[] = [
  { value: "created", label: "Created" },
  { value: "saved", label: "Saved" },
];

const characterFilterOptions: TabOption[] = [
  { value: "model", label: "Models" },
  { value: "animal", label: "Animals" },
];

interface HeaderProps {
  addGroup?: () => void;
  className?: string;
  newCardsCount?: number;
  processingCount?: number;
  imageSelection?: UseImageBatchSelectionReturn;
  onImageBatchDeleted?: () => void;
  videoSelection?: UseImageBatchSelectionReturn;
  onVideoBatchDeleted?: () => void;
}

export const Header = ({
  className,
  newCardsCount = 0,
  processingCount = 0,
  imageSelection,
  onImageBatchDeleted,
  videoSelection,
  onVideoBatchDeleted,
}: HeaderProps) => {
  const dispatch = useAppDispatch();
  const {
    focusType,
    videoType,
    productType,
    characterType,
    setFocusType,
    setVideoType,
    setProductType,
    setCharacterType,
  } = useLibraryFilters();

  const imageSelectedCount = imageSelection?.selectedCount ?? 0;
  const videoSelectedCount = videoSelection?.selectedCount ?? 0;
  const showBatchDelete =
    (focusType === "image" && imageSelectedCount > 0) ||
    (focusType === "video" && videoType !== "saved" && videoSelectedCount > 0);

  const handleBatchDelete = useSerializeCallback(async () => {
    if (focusType === "image") {
      if (!imageSelection || imageSelectedCount === 0) return;
      try {
        await moveAssetsToTrash({ ids: [...imageSelection.selectedIds] });
        message.success(t("Moved to trash."));
        imageSelection.clear();
        onImageBatchDeleted?.();
      } catch (err: any) {
        showFallbackErrorToast(err, t("Failed to move to trash, please try again."));
      }
    } else if (focusType === "video") {
      if (!videoSelection || videoSelectedCount === 0) return;
      try {
        await moveAssetsToTrash({ ids: [...videoSelection.selectedIds] });
        message.success(t("Moved to trash."));
        videoSelection.clear();
        onVideoBatchDeleted?.();
      } catch (err: any) {
        showFallbackErrorToast(err, t("Failed to move to trash, please try again."));
      }
    }
  });

  // Video tab: 有进行中任务时显示数字角标
  const tabOptionsWithBadge = tabOptions.map((opt) => {
    if (opt.value === "video") {
      return { ...opt, badge: processingCount };
    }
    return opt;
  });

  // Generated tab: 有新未查看产物时显示小红点
  const contentFilterOptions = contentFilterBaseOptions.map((opt) => {
    if (opt.value === "generated") {
      return { ...opt, dot: newCardsCount > 0 };
    }
    return opt;
  });

  return (
    <div className={classNames("filterWrap", className)}>
      <div className="flex justify-between gap-[16px]">
        <Tabs
          value={focusType}
          options={tabOptionsWithBadge}
          onChange={(value) => {
            setFocusType(value as FocusType);
          }}
        />
        <div className="flex items-center gap-[8px]">
          {showBatchDelete && (
            <Button
              variant="outline"
              size="md"
              loading={handleBatchDelete.isPending()}
              icon={<IconBatchDelete className="size-5 text-kds-fg group-hover:text-kds-accent" />}
              iconPlacement="start"
              onClick={handleBatchDelete}
              className="group"
            >
              <Trans>Batch Delete</Trans>
            </Button>
          )}
          <CommonButton size="middle" onClick={() => dispatch(showTrashDisplay())}>
            <IconLineTrash className="w-[16px] h-[16px] [&_path]:stroke-current" />
            <Trans>Trash</Trans>
          </CommonButton>
        </div>
      </div>
      {focusType === "video" && (
        <div className="flex flex-row items-center gap-[16px] mt-[16px]">
          <Tabs
            type="text"
            value={videoType}
            options={contentFilterOptions}
            onChange={(value) => {
              setVideoType(value as VideoType);
            }}
          />
        </div>
      )}
      {focusType === "product" && (
        <div className="flex flex-row items-center gap-[16px] mt-[16px]">
          <Tabs
            type="text"
            value={productType}
            options={productFilterOptions}
            onChange={(value) => {
              setProductType(value as ProductType);
            }}
          />
        </div>
      )}
      {focusType === "character" && (
        <div className="flex flex-row items-center gap-[16px] mt-[16px]">
          <Tabs
            type="text"
            value={characterType}
            options={characterFilterOptions}
            onChange={(value) => {
              setCharacterType(value as CharacterType);
            }}
          />
        </div>
      )}
    </div>
  );
};
